import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';

import { BillingCycle, DocumentType } from '@prisma/client';

import { useMutation } from '@redwoodjs/web';

import { AutoCompleteOptionType } from 'src/components/FormikComponents/NPIAutoComplete';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DebitFor, Platform } from 'src/constants/direct-debit';
import { SEND_SEPA_MANDATE } from 'src/graphql/mutations/merchants.mutation';
import { LogError } from 'src/lib';
import { ContactInfo, ResetFormFunc, SetFieldFunc } from 'src/types/direct-debit';
import { getMerchants, GetPartners } from 'types/graphql';

export type UseSendSepaDDProps = {
    onSendSepaDD: () => void;
    onValidateEmail: (email: string, onError?: () => void, onSuccess?: () => void) => Promise<void>;
    partnersData: GetPartners;
    merchantsData: getMerchants;
    resetFormik?: () => void;
};

export type DebitData = {
    documentType: DocumentType;
    emailAddress: string;
    firstName: string;
    lastName: string;
    merchantId?: string;
    partnerId?: string;
    typeOfPayment: BillingCycle;
    useExistingBankDetails: boolean;
    debitFor: DebitFor;
};

export type UseSendSepaDDResponse = {
    isOpen: boolean;
    debitData: DebitData;
    directDebitFor: DebitFor;
    platform: Platform;
    isSubmitted: boolean;
    isSubmiting: boolean;
    isUsingExistingBankDetails: boolean;
    sendMandate: () => void;
    handleClose: () => void;
    bankChecked: (
        event: ChangeEvent<HTMLInputElement>,
        setFieldValue: (field: string, value: string, shouldValidate: boolean) => Promise<unknown>,
        contactInfo: ContactInfo
    ) => void;
    setDirectDebitFor: Dispatch<SetStateAction<DebitFor>>;
    setPlatform: Dispatch<SetStateAction<Platform>>;
    displayDirectDebitModal: (input: any) => Promise<void>;
    setIsSubmitted: Dispatch<SetStateAction<boolean>>;
    setIsUsingExistingBankDetails: Dispatch<SetStateAction<boolean>>;
    resetFields: (
        setFieldValue: SetFieldFunc,
        resetForm: ResetFormFunc,
        forValue: DebitFor,
        platform?: Platform
    ) => void;
    clearExistingContactInfo: (
        setFieldValue: SetFieldFunc,
        resetForm: ResetFormFunc,
        debitFor: DebitFor,
        platform: Platform,
        setIsUsingExistingBankDetails: React.Dispatch<React.SetStateAction<boolean>>
    ) => void;
    merchantSelected: (
        value: AutoCompleteOptionType,
        setFieldValue: SetFieldFunc,
        isUsingExistingBankDetails: boolean
    ) => void;
    partnerSelected: (
        value: AutoCompleteOptionType,
        setFieldValue: SetFieldFunc,
        isUsingExistingBankDetails: boolean
    ) => void;
    merchantContactInfo: ContactInfo;
    setMerchantContactInfo: Dispatch<SetStateAction<ContactInfo>>;
    partner: AutoCompleteOptionType;
    setPartner: Dispatch<SetStateAction<AutoCompleteOptionType>>;
    partnerContactInfo: ContactInfo;
    setPartnerContactInfo: Dispatch<SetStateAction<ContactInfo>>;
    selectedMerchantOption: AutoCompleteOptionType;
    setSelectedMerchantOption: Dispatch<SetStateAction<AutoCompleteOptionType>>;
    setSelectedMerchantAccountNumberOption: Dispatch<SetStateAction<AutoCompleteOptionType>>;
    selectedMerchantAccountNumberOption: AutoCompleteOptionType;
    isSendingMandate: boolean;
};

function useSendSepaDD({
    onSendSepaDD,
    onValidateEmail,
    merchantsData,
    partnersData,
    resetFormik,
}: UseSendSepaDDProps): UseSendSepaDDResponse {
    const [selectedMerchantOption, setSelectedMerchantOption] = useState<AutoCompleteOptionType>();
    const [selectedMerchantAccountNumberOption, setSelectedMerchantAccountNumberOption] =
        useState<AutoCompleteOptionType>();
    const [merchantContactInfo, setMerchantContactInfo] = useState<ContactInfo>();
    const [partner, setPartner] = useState<AutoCompleteOptionType>();
    const [partnerContactInfo, setPartnerContactInfo] = useState<ContactInfo>();

    const [debitData, setDebitData] = useState<DebitData>();
    const [isOpen, setIsOpen] = useState(false);
    const [directDebitFor, setDirectDebitFor] = useState(DebitFor.MERCHANT);
    const [platform, setPlatform] = useState(Platform.BUDDY);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [isUsingExistingBankDetails, setIsUsingExistingBankDetails] = useState(false);

    const [sendSepaMandate, { loading: isSendingMandate }] = useMutation(SEND_SEPA_MANDATE, {
        onError: (error) => {
            showErrorToast(error?.message);
            LogError(error);
        },
        onCompleted: () => {
            showSuccessToast('Direct Debit Mandate Sent');
            // Reset the form and clear state
            if (resetFormik) {
                resetFormik();
            }
            setIsSubmitted(false);
            setIsUsingExistingBankDetails(false);
            setSelectedMerchantOption(null);
            setSelectedMerchantAccountNumberOption(null);
            setMerchantContactInfo(null);
            setPartner(null);
            setPartnerContactInfo(null);
            setDirectDebitFor(DebitFor.MERCHANT);
            setPlatform(Platform.BUDDY);
            onSendSepaDD();
        },
    });

    const displayDirectDebitModal = async (input) => {
        const inputCopy = { ...input };
        delete inputCopy.merchant;
        delete inputCopy.directDebitFor;
        delete inputCopy.partner;
        const postData: DebitData = {
            ...inputCopy,
            partnerId: Number.parseInt(input.partnerId, 10),
            merchantId: Number.parseInt(input.merchantId, 10),
            useExistingBankDetails: isUsingExistingBankDetails,
            documentType:
                directDebitFor === DebitFor.MERCHANT
                    ? DocumentType.MERCHANT_SEPA_DD
                    : DocumentType.PARTNER_SEPA_DD,
        };
        if (isUsingExistingBankDetails) {
            setDebitData(postData);
            setIsOpen(true);
            return;
        }
        setIsSubmiting(true);
        await onValidateEmail(
            input.emailAddress,
            () => {
                setIsSubmiting(false);
            },
            () => {
                setIsSubmiting(false);
                setDebitData(postData);
                setIsOpen(true);
            }
        );
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const sendMandate = () => {
        const partnerIdParsed = Number.parseInt(debitData?.partnerId, 10);
        const partnerId = !Number.isNaN(partnerIdParsed) ? partnerIdParsed : null;
        const merchantIdParsed = Number.parseInt(debitData?.merchantId, 10);
        const merchantId = !Number.isNaN(merchantIdParsed) ? merchantIdParsed : null;
        const debitDataInput = debitData;

        delete debitDataInput.debitFor;
        const inputData = {
            ...debitDataInput,
            partnerId,
            merchantId,
        };
        sendSepaMandate({
            variables: {
                input: inputData,
            },
        });
    };

    const setFormFieldsForContactInfo = (contactInfo: ContactInfo, setFieldValue: SetFieldFunc) => {
        setFieldValue('firstName', contactInfo.firstName);
        setFieldValue('lastName', contactInfo.lastName);
        setFieldValue('emailAddress', contactInfo.emailAddress);
        setFieldValue('merchantId', contactInfo.merchantId?.toString());
        setFieldValue('partnerId', contactInfo.partnerId?.toString());
    };

    const bankChecked = (event, setFieldValue: SetFieldFunc, contactInfo: ContactInfo) => {
        const { checked } = event.target;
        if (checked) {
            setFormFieldsForContactInfo(contactInfo, setFieldValue);
            setIsUsingExistingBankDetails(true);
        } else {
            setIsUsingExistingBankDetails(false);
        }
    };

    const partnerSelected = (
        value: AutoCompleteOptionType,
        setFieldValue: SetFieldFunc,
        isUsingExistingBankDetails: boolean
    ) => {
        const selectedPartner = partnersData?.partners.find((ptr) => {
            return ptr.id === value.id;
        });
        if (!selectedPartner) {
            return;
        }
        const { firstName, lastName, email, id } = selectedPartner;
        const contactInfo: ContactInfo = {
            firstName,
            lastName,
            emailAddress: email,
            partnerId: id,
        };
        setPartnerContactInfo(contactInfo);

        if (isUsingExistingBankDetails) {
            setFormFieldsForContactInfo(contactInfo, setFieldValue);
        } else {
            setFieldValue('firstName', '');
            setFieldValue('lastName', '');
            setFieldValue('emailAddress', '');
        }
        setFieldValue('partnerId', id?.toString());
    };

    const merchantSelected = (
        value: AutoCompleteOptionType,
        setFieldValue: SetFieldFunc,
        isUsingExistingBankDetails: boolean
    ) => {
        const selectedMerchant = merchantsData.merchants.data.find((mch) => {
            return mch.id === (value?.id ?? value?.value);
        });

        if (!selectedMerchant) {
            return;
        }

        setSelectedMerchantOption({
            id: selectedMerchant?.id,
            label: selectedMerchant?.tradingName || selectedMerchant?.merchantContactName,
            value: selectedMerchant?.tradingName || selectedMerchant?.merchantContactName,
        });
        setSelectedMerchantAccountNumberOption({
            label: selectedMerchant?.merchantAccountNumber,
            value: selectedMerchant.id,
        });

        const { email, merchantContactName, id } = selectedMerchant;
        const [lastName, firstName] = merchantContactName.split(' ');
        const contactInfo: ContactInfo = {
            firstName,
            lastName,
            emailAddress: email,
            merchantId: id,
        };
        setMerchantContactInfo(contactInfo);
        setFieldValue('merchantId', id?.toString());

        if (isUsingExistingBankDetails) {
            setFormFieldsForContactInfo(contactInfo, setFieldValue);
        } else {
            setFieldValue('firstName', '');
            setFieldValue('lastName', '');
            setFieldValue('emailAddress', '');
        }
    };

    const resetFields = (
        setFieldValue: SetFieldFunc,
        resetForm: ResetFormFunc,
        forValue: DebitFor,
        platform?: Platform
    ) => {
        resetForm();
        setIsSubmitted(false);
        setIsUsingExistingBankDetails(false);
        setFieldValue('directDebitFor', forValue);
        setFieldValue('debitFor', forValue);
        setFieldValue('merchant', null);
        setSelectedMerchantOption(null);
        setSelectedMerchantAccountNumberOption(null);
        setFieldValue('partner', null);
        setPartner(null);
        if (platform) {
            setFieldValue('platform', platform);
        }
    };

    const clearExistingContactInfo = (
        setFieldValue: SetFieldFunc,
        resetForm: ResetFormFunc,
        debitFor: DebitFor,
        platform: Platform,
        setIsUsingExistingBankDetails: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        resetForm();
        setFieldValue('firstName', '');
        setFieldValue('lastName', '');
        setFieldValue('emailAddress', '');
        setFieldValue('merchantId', '');
        setFieldValue('partnerId', '');
        setFieldValue('directDebitFor', debitFor);
        setFieldValue('platform', platform);
        setIsUsingExistingBankDetails(false);
    };

    return {
        isOpen,
        debitData,
        directDebitFor,
        platform,
        isSubmitted,
        isSubmiting,
        isUsingExistingBankDetails,
        sendMandate,
        handleClose,
        bankChecked,
        setDirectDebitFor,
        setPlatform,
        displayDirectDebitModal,
        setIsSubmitted,
        setIsUsingExistingBankDetails,
        resetFields,
        clearExistingContactInfo,
        merchantSelected,
        setSelectedMerchantOption,
        setSelectedMerchantAccountNumberOption,
        partnerSelected,
        selectedMerchantAccountNumberOption,
        selectedMerchantOption,
        merchantContactInfo,
        setMerchantContactInfo,
        partner,
        setPartner,
        setPartnerContactInfo,
        partnerContactInfo,
        isSendingMandate,
    };
}

export default useSendSepaDD;
